import { useSyncExternalStore } from 'react';

/**
 * What the page knows about its own connection, for every page at once
 * (roadmap item pagines-que-diuen-que-carreguen): whether the device is
 * online, whether the platform answered the last request, and whether what
 * the page shows came from the copy this device kept of the last answer
 * (`storedReads.ts`) instead of from the platform.
 *
 * `apiFetch` reports every answer here, so a page needs no code of its own to
 * be told it is offline: the shell's notice (`ConnectionNotice`) reads it.
 *
 * Only two things say the connection is gone: the browser's own word
 * (`navigator.onLine`, the `offline` event) and a request that found nothing
 * (a network error, a proxy's gateway page). A slow answer never does: a page
 * that takes long on a desktop said "no connection" and then "the connection
 * is back" without it ever going (web card 226, 2026-10-19).
 */
export interface Connectivity {
  /** The browser's own word: false while the device has no network at all. */
  online: boolean;
  /** The last request found no platform: a network error, a proxy's 5xx. */
  failing: boolean;
  /**
   * The connection came back while this page was showing it gone (the
   * browser's `online` event, or an answer after a failure): the notice says
   * so until the page changes or is read again (`freshAgain`).
   */
  back: boolean;
  /**
   * When the oldest stored copy this page is showing was stored (epoch ms),
   * or null while everything on it came from the platform. Cleared when the
   * page changes or a page says it read everything again (`freshAgain`).
   */
  storedAt: number | null;
  /**
   * When the platform last answered (epoch ms) before the connection went,
   * or null before it ever has. Taken when the connection goes, not on every
   * answer, so a page is not drawn again for each request it makes.
   */
  lastAnswerAt: number | null;
  /**
   * How many notices of a page's own are drawn (the session page reads its
   * stream and says more than the shell can): while one is, the shell's stays
   * hidden, so a page never shows two.
   */
  ownNotices: number;
}

let state: Connectivity = {
  online: typeof navigator === 'undefined' || navigator.onLine !== false,
  failing: false,
  back: false,
  storedAt: null,
  lastAnswerAt: null,
  ownNotices: 0,
};
const listeners = new Set<() => void>();
/** The latest answer, kept out of the state until the connection goes. */
let lastAnswer: number | null = null;

function set(next: Partial<Connectivity>) {
  const merged = { ...state, ...next };
  if ((Object.keys(merged) as (keyof Connectivity)[]).every((k) => merged[k] === state[k])) return;
  state = merged;
  listeners.forEach((l) => l());
}

export const connectivity = {
  get: () => state,
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** The platform answered (any status below 500), fresh from the network. */
  answered(now = Date.now()) {
    lastAnswer = now;
    set({ failing: false, online: true, back: state.back || down() });
  },
  /** A kept copy answered, stored at `storedAt`: the platform did not. */
  servedStored(storedAt: number) {
    set({
      failing: true,
      lastAnswerAt: lastAnswer,
      storedAt: state.storedAt === null ? storedAt : Math.min(state.storedAt, storedAt),
    });
  },
  /** No platform: the request failed on the network, or a proxy answered 5xx. */
  unreachable() {
    set({ failing: true, lastAnswerAt: lastAnswer });
  },
  /**
   * The browser's own word. Back online, nothing is known to be failing any
   * more: the notice says the connection is back at once, and the request the
   * shell sends then (`useConnectionWatch`) says otherwise if the platform is
   * still not there.
   */
  online(online: boolean) {
    set(
      online
        ? { online, failing: false, back: state.back || down() }
        : { online, lastAnswerAt: lastAnswer },
    );
  },
  /** A new page, or one that read all it shows again: nothing on it is a stored copy any more. */
  freshAgain() {
    set({ storedAt: null, back: false });
  },
  /** A page draws its own notice until the returned function is called. */
  ownNotice() {
    set({ ownNotices: state.ownNotices + 1 });
    return () => set({ ownNotices: Math.max(0, state.ownNotices - 1) });
  },
  /** For tests. */
  reset(next: Partial<Connectivity> = {}) {
    lastAnswer = null;
    state = {
      online: true,
      failing: false,
      back: false,
      storedAt: null,
      lastAnswerAt: null,
      ownNotices: 0,
      ...next,
    };
    listeners.forEach((l) => l());
  },
};

/** Whether the page is showing the connection gone right now. */
function down(): boolean {
  return !state.online || state.failing;
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => connectivity.online(true));
  window.addEventListener('offline', () => connectivity.online(false));
}

export function useConnectivity(): Connectivity {
  return useSyncExternalStore(connectivity.subscribe, connectivity.get, connectivity.get);
}

/**
 * What the notice says: nothing, no network on the device, no platform, a page
 * of stored copies now that both are back, or that the connection came back.
 */
export type NoticeKind = 'none' | 'offline' | 'unreachable' | 'stored' | 'back';

export function noticeOf(c: Connectivity): NoticeKind {
  if (!c.online) return 'offline';
  if (c.failing) return 'unreachable';
  if (c.storedAt !== null) return 'stored';
  if (c.back) return 'back';
  return 'none';
}

/** Whether the page has no connection to the platform now, by either word. */
export function isDown(c: Connectivity): boolean {
  return !c.online || c.failing;
}

/** Since when what the page shows is known: the stored copy's time, else the platform's last answer. */
export function shownSince(c: Connectivity): number | null {
  return c.storedAt ?? c.lastAnswerAt;
}
